/**
 * Topic Selector Component
 * Hierarchical navigation for chatbot topics - mirrors sidebar structure
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronLeft, Check, Settings, DollarSign, Users, Leaf } from 'lucide-react';
import { topicHierarchy, sectionNames } from './chatbotConfig';

// Icon mapping for areas
const areaIcons = {
  'area-operacoes': <Settings className="w-4 h-4" />,
  'area-financas': <DollarSign className="w-4 h-4" />,
  'area-rh': <Users className="w-4 h-4" />,
  'area-esg': <Leaf className="w-4 h-4" />,
};

const TopicSelector = ({ 
  language, 
  onSelectTopic, 
  onSendMessage,
  initialSection // The section to start navigation at (e.g., 'mina-acesso-2026')
}) => {
  // Helper function to find the path to a section in the hierarchy (including the section itself if it has children)
  const findPathToSection = (sectionId) => {
    if (!sectionId) return [];
    
    const findPath = (items, targetId, currentPath = []) => {
      for (const item of items) {
        if (item.id === targetId) {
          // If the target has children, include it in the path so we show its children
          // If it's a leaf node, return the path to its parent level
          if (item.children && item.children.length > 0) {
            return [...currentPath, item.id];
          }
          return currentPath;
        }
        if (item.children) {
          const found = findPath(item.children, targetId, [...currentPath, item.id]);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findPath(topicHierarchy, sectionId) || [];
  };

  // Navigation state - tracks current path through hierarchy
  // Initialize with path to initialSection if provided
  const [navigationPath, setNavigationPath] = useState(() => findPathToSection(initialSection));

  // Update navigation path when initialSection changes (e.g., when returning from a conversation)
  React.useEffect(() => {
    if (initialSection) {
      const path = findPathToSection(initialSection);
      setNavigationPath(path);
    }
  }, [initialSection]);
  
  // Get current level items based on navigation path
  const getCurrentItems = () => {
    let items = topicHierarchy;
    
    for (const pathItem of navigationPath) {
      const found = items.find(item => item.id === pathItem);
      if (found && found.children) {
        items = found.children;
      } else {
        break;
      }
    }
    
    return items;
  };
  
  // Get breadcrumb trail
  const getBreadcrumbs = () => {
    const breadcrumbs = [];
    let items = topicHierarchy;
    
    for (const pathItem of navigationPath) {
      const found = items.find(item => item.id === pathItem);
      if (found) {
        breadcrumbs.push({
          id: found.id,
          name: found.name[language] || found.name.pt
        });
        if (found.children) {
          items = found.children;
        }
      }
    }
    
    return breadcrumbs;
  };
  
  // Handle item click
  const handleItemClick = (item) => {
    // Don't allow clicking disabled items
    if (item.disabled) {
      return;
    }
    
    if (item.children && item.children.length > 0) {
      // Has children - navigate into this level
      setNavigationPath([...navigationPath, item.id]);
    } else {
      // Leaf node - select as topic
      onSelectTopic(item.id);
    }
  };
  
  // Handle back navigation
  const handleBack = () => {
    setNavigationPath(navigationPath.slice(0, -1));
  };
  
  // Handle breadcrumb click
  const handleBreadcrumbClick = (index) => {
    setNavigationPath(navigationPath.slice(0, index));
  };
  
  const currentItems = getCurrentItems();
  const breadcrumbs = getBreadcrumbs();
  const isAtRoot = navigationPath.length === 0;

  return (
    <div>
      {/* Header with breadcrumbs */}
      <div className="mb-3">
        {isAtRoot ? (
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
            {language === 'pt' ? 'Seleccione a área de análise' : 'Select analysis area'}
          </p>
        ) : (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBack();
              }}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
              data-testid="topic-back-btn"
            >
              <ChevronLeft className="w-3 h-3" />
              <span>{language === 'pt' ? 'Voltar' : 'Back'}</span>
            </button>
            <span className="text-xs text-slate-300 mx-1">|</span>
            <div className="flex items-center gap-1 text-xs text-slate-500 flex-wrap">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleBreadcrumbClick(0);
                }}
                className="hover:text-primary transition-colors"
              >
                {language === 'pt' ? 'Início' : 'Home'}
              </button>
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={crumb.id}>
                  <ChevronRight className="w-3 h-3 text-slate-300" />
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBreadcrumbClick(idx + 1);
                    }}
                    className={`hover:text-primary transition-colors ${idx === breadcrumbs.length - 1 ? 'text-primary font-medium' : ''}`}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Current level items */}
      <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
        {currentItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isDisabled = item.disabled === true;
          const itemIcon = areaIcons[item.id] || item.icon;
          
          return (
            <button
              key={item.id}
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(item);
              }}
              disabled={isDisabled}
              className={`w-full text-left text-sm rounded-lg px-4 py-3 transition-all border flex items-center justify-between group
                ${isDisabled 
                  ? 'text-slate-400 bg-slate-100 border-slate-200 cursor-not-allowed opacity-60'
                  : hasChildren 
                    ? 'text-primary bg-slate-50 hover:bg-slate-100 border-slate-200' 
                    : 'text-slate-700 bg-white hover:bg-emerald-50 border-slate-200 hover:border-emerald-300'
                }`}
              data-testid={`topic-${item.id}`}
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  {itemIcon && (
                    <span className={`${isDisabled ? 'text-slate-300' : 'text-slate-500 group-hover:text-primary'} transition-colors`}>
                      {itemIcon}
                    </span>
                  )}
                  <span className="font-medium">{item.name[language] || item.name.pt}</span>
                </div>
                {isDisabled && item.disabledMessage && (
                  <span className="text-xs text-slate-400 mt-1 ml-6">
                    {item.disabledMessage[language] || item.disabledMessage.pt}
                  </span>
                )}
              </div>
              {!isDisabled && (
                hasChildren ? (
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
                ) : (
                  <Check className="w-4 h-4 text-transparent group-hover:text-emerald-500 transition-colors" />
                )
              )}
            </button>
          );
        })}
        
        {/* Custom question option - only at root level */}
        {isAtRoot && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelectTopic('custom');
            }}
            className="w-full text-left text-sm text-slate-500 bg-white hover:bg-slate-50 rounded-lg px-4 py-3 transition-colors border border-dashed border-slate-300 mt-2"
            data-testid="topic-custom"
          >
            {language === 'pt' ? 'Fazer pergunta personalizada' : 'Ask custom question'}
          </button>
        )}
      </div>
      
      {/* Helper text */}
      {!isAtRoot && (
        <p className="text-[10px] text-slate-400 mt-3 text-center">
          {language === 'pt' 
            ? 'Seleccione uma opção ou volte atrás para escolher outro tema' 
            : 'Select an option or go back to choose another topic'}
        </p>
      )}
    </div>
  );
};

export default TopicSelector;
